import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { movies } from "@/mocks/movies";
import { categories } from "@/mocks/categories";
import { newsList } from "@/mocks/news";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import MovieCard from "@/components/feature/MovieCard";
import NewsCard from "@/components/feature/NewsCard";
import SectionTitle from "@/components/feature/SectionTitle";

const latestMovies = [...movies]
  .sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1))
  .slice(0, 10);

export default function Home() {
  usePageMeta({
    title: "电影天堂 - 最新电影资源与影视资讯平台",
    description:
      "电影天堂为您提供丰富的电影资源和最新影视资讯，覆盖各类电影类型，让用户轻松发现和了解热门影片内容。",
    keywords: "电影天堂,电影资源,影视资讯,最新电影,电影推荐,高清电影,影视平台",
  });

  const featuredNews = newsList[0];
  const sideNews = newsList.slice(1, 5);

  return (
    <div>
      <HeroCarousel />

      {/* 分类快捷入口 */}
      <section className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
        <SectionTitle
          title="电影分类"
          subtitle="按类型快速进入你感兴趣的片单"
          icon="ri-apps-2-line"
        />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-primary-600 transition-colors group-hover:bg-accent-100 group-hover:text-accent-700">
                <i className={`${c.icon} text-xl`}></i>
              </span>
              <div className="min-w-0">
                <p className="font-heading text-sm font-bold text-foreground-950">{c.name}</p>
                <p className="line-clamp-1 text-xs text-foreground-500">
                  {c.description.slice(0, 10)}…
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 最新电影资源 */}
      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <SectionTitle
            title="最新电影资源推荐"
            subtitle="按上线时间倒序，为你更新近期值得关注的影片"
            icon="ri-clapperboard-line"
            moreTo="/category/all"
          />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
            {latestMovies.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        </div>
      </section>

      {/* 热门影视资讯 */}
      <section className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
        <SectionTitle
          title="热门影视资讯"
          subtitle="精选最新影视动态与行业观察"
          icon="ri-newspaper-line"
          moreTo="/news"
        />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <NewsCard item={featuredNews} />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1">
            {sideNews.map((n) => (
              <NewsCard key={n.id} item={n} compact />
            ))}
          </div>
        </div>
      </section>

      {/* 网站简介 */}
      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                关于电影天堂
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                电影天堂聚焦电影资源信息与影视资讯发布，致力于为电影爱好者提供内容丰富、分类细致、资讯及时的浏览体验。
                无论你想快速查找最新影片、按类型筛选片单，还是了解影视行业动态，都能在这里轻松找到答案。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
                >
                  <i className="ri-information-line"></i>
                  了解我们
                </Link>
                <Link
                  to="/news"
                  className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  <i className="ri-newspaper-line"></i>
                  浏览资讯
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 lg:grid-cols-1">
              {[
                { label: "收录影片", value: `${movies.length}+`, icon: "ri-film-line" },
                { label: "电影类型", value: `${categories.length}`, icon: "ri-apps-2-line" },
                { label: "资讯文章", value: `${newsList.length}+`, icon: "ri-article-line" },
              ].map((s) => (
                <div
                  key={s.label}
                  className="rounded-lg border border-background-200 bg-background-100 p-4 lg:flex lg:items-center lg:gap-4"
                >
                  <span className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-accent-700 lg:mb-0">
                    <i className={`${s.icon} text-lg`}></i>
                  </span>
                  <div>
                    <p className="font-heading text-xl font-black text-foreground-950">{s.value}</p>
                    <p className="text-xs text-foreground-600">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}